import { BarChartOutlined } from '@ant-design/icons';
import { Alert, Descriptions, Modal, Typography } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { useCachedDataset, useChartSpec } from '../../../../api/Numina';
import Loading from '../../../../components/Loading';
import PlotlyChartRenderer from '../ToolRenderers/components/PlotlyChartRenderer';

const { Text } = Typography;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    max-height: 80vh;
  }
  .ant-modal-body {
    max-height: calc(80vh - 110px);
    overflow-y: auto;
  }
`;

const StyledDescriptions = styled(Descriptions)`
  margin-bottom: 16px;
`;

const ChartContainer = styled.div`
  margin: 16px 0;
  padding: 16px;
  background: #fafafa;
  border-radius: 4px;
`;

function ChartModal({ chartName = null, open, onClose }) {
  const { data: chartData, isLoading: chartLoading } = useChartSpec(chartName, {
    enabled: open && !!chartName,
  });

  const chart = chartData?.data;

  // Fetch the underlying dataset to get the data for rendering
  const { data: datasetData, isLoading: datasetLoading } = useCachedDataset(chart?.result_key, {
    enabled: open && !!chart?.result_key,
  });

  const dataset = datasetData?.data;
  const isLoading = chartLoading || datasetLoading;

  // Parse spec if it's a JSON string
  let parsedSpec = chart?.spec;
  if (typeof parsedSpec === 'string') {
    try {
      parsedSpec = JSON.parse(parsedSpec);
    } catch (e) {
      parsedSpec = {};
    }
  }

  const chartSpec = parsedSpec || {};
  const chartType = chart?.chart_type || '';
  const encodings = chartSpec?.encodings || {};

  // Use full_rows if available, otherwise use sample_rows
  const chartDataRows = dataset?.full_rows || dataset?.sample_rows || [];
  const hasValidData = chartDataRows && Array.isArray(chartDataRows) && chartDataRows.length > 0;

  return (
    <StyledModal
      title={
        <>
          <BarChartOutlined /> {chart?.title || `Chart: ${chartName}`}
        </>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      {(() => {
        if (isLoading) {
          return <Loading />;
        }
        if (!chart) {
          return <Alert message="Chart not found" type="error" />;
        }
        return (
          <>
            <StyledDescriptions bordered column={1} size="small">
              <Descriptions.Item label="Chart Name">
                <Text code>{chart.chart_name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Chart Type">
                <Text>{chart.chart_type}</Text>
              </Descriptions.Item>
              {chart.section_title && (
                <Descriptions.Item label="Section">{chart.section_title}</Descriptions.Item>
              )}
              <Descriptions.Item label="Dataset Key">
                <Text code>{chart.result_key}</Text>
              </Descriptions.Item>
              {chart.description && (
                <Descriptions.Item label="Description">{chart.description}</Descriptions.Item>
              )}
              <Descriptions.Item label="Created">
                {new Date(chart.created_at).toLocaleString()}
              </Descriptions.Item>
            </StyledDescriptions>

            {hasValidData ? (
              <ChartContainer>
                <PlotlyChartRenderer
                  spec={{ ...chartSpec, encodings }}
                  data={chartDataRows}
                  chartType={chartType}
                  title={chart.title}
                />
              </ChartContainer>
            ) : (
              <Alert
                message="No chart data available"
                description="Chart specification exists but the underlying dataset could not be loaded."
                type="warning"
              />
            )}
          </>
        );
      })()}
    </StyledModal>
  );
}

ChartModal.propTypes = {
  chartName: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ChartModal;
